import { Page } from '../../base/Page';
import { useProducts } from '../../base/useDbHooks';
import { useBatchProductPricing, useCustomerPreOrders } from '../../base/useDbPreOrderHooks';
import { Card, Table } from 'flowbite-react';
import { useMemo } from 'react';
import { DataSnapshot } from 'firebase/database';

interface PreOrderItemSummary {
    productId: string;
    pricingId: string;
    totalQuantity: number;
}

export default function PreOrderItemsSummary() {
    const { products } = useProducts();
    const { customerPreOrders, loading } = useCustomerPreOrders();

    // Group pre-order items by productId and pricingId
    const itemSummaries = useMemo(() => {
        const summariesMap = new Map<string, PreOrderItemSummary>();

        customerPreOrders.forEach((preOrder) => {
            preOrder.items.forEach((item) => {
                const key = `${item.productId}-${item.pricingId}`;
                const existing = summariesMap.get(key) || {
                    productId: item.productId,
                    pricingId: item.pricingId,
                    totalQuantity: 0,
                };
                existing.totalQuantity += item.quantity;
                summariesMap.set(key, existing);
            });
        });

        return Array.from(summariesMap.values());
    }, [customerPreOrders]);

    // Memoize product IDs for pricing fetch
    const productIds = useMemo(
        () => Array.from(new Set(itemSummaries.map((summary) => summary.productId))),
        [itemSummaries]
    );

    // Fetch pricing for all product IDs
    const { pricingSnapshotsMap } = useBatchProductPricing(productIds);

    return (
        <Page title="Pre-Order Items Summary">
            <div className="container mx-auto py-8">
                <h1 className="uppercase text-2xl font-bold text-center mb-6">
                    Pre-Order Items Summary
                </h1>
                <Card>
                    {loading ? (
                        <div>Loading...</div>
                    ) : (
                        <Table>
                            <Table.Head>
                                <Table.HeadCell>Product</Table.HeadCell>
                                <Table.HeadCell>Pricing</Table.HeadCell>
                                <Table.HeadCell>Total Quantity</Table.HeadCell>
                            </Table.Head>
                            <Table.Body>
                                {itemSummaries.map((summary) => {
                                    const product = products.find((p) => p.productId === summary.productId);
                                    const pricingSnapshots = pricingSnapshotsMap.get(summary.productId) || [];
                                    const pricing = pricingSnapshots
                                        .filter((snapshot): snapshot is DataSnapshot & { key: string } => snapshot.key !== null)
                                        .find((snapshot) => snapshot.key === summary.pricingId)?.val();

                                    return (
                                        <Table.Row key={`${summary.productId}-${summary.pricingId}`}>
                                            <Table.Cell>{product?.productNameEn || 'Unknown Product'}</Table.Cell>
                                            <Table.Cell>
                                                {pricing ? `${pricing.unit} - ${pricing.price}円` : 'Pricing Loading...'}
                                            </Table.Cell>
                                            <Table.Cell>{summary.totalQuantity}</Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    )}
                </Card>
            </div>
        </Page>
    );
}
